import { z } from 'zod';
import { createListQuerySchema } from './shared/pagination.js';

export const AdminTwitterWindowStatsSchema = z.object({
  newTweets: z.number().int(),
  updatedTweets: z.number().int(),
  newUsers: z.number().int(),
  updatedUsers: z.number().int(),
  impressions: z.number().int(),
  bookmarks: z.number().int(),
});

export type AdminTwitterWindowStats = z.infer<typeof AdminTwitterWindowStatsSchema>;

export const AdminTwitterStorageStatsSchema = z.object({
  tweetsTableSize: z.string(),
  usersTableSize: z.string(),
  mediaTableSize: z.string(),
  totalSize: z.string(),
});

export type AdminTwitterStorageStats = z.infer<typeof AdminTwitterStorageStatsSchema>;

export const AdminTwitterLastSyncSchema = z.object({
  tweets: z.date().nullable(),
  users: z.date().nullable(),
  timeline: z.date().nullable(),
});

export type AdminTwitterLastSync = z.infer<typeof AdminTwitterLastSyncSchema>;

export const AdminTwitterStatsResponseSchema = z.object({
  totalTweets: z.number().int(),
  totalUsers: z.number().int(),
  totalMedia: z.number().int(),
  totalLikes: z.number().int(),
  totalBookmarks: z.number().int(),
  totalTweetImpressions: z.number().int(),
  totalTweetBookmarks: z.number().int(),
  last24h: AdminTwitterWindowStatsSchema,
  lastHour: AdminTwitterWindowStatsSchema,
  storage: AdminTwitterStorageStatsSchema,
  lastSync: AdminTwitterLastSyncSchema,
});

export type AdminTwitterStatsResponse = z.infer<typeof AdminTwitterStatsResponseSchema>;

export const AdminTwitterUsageSummarySchema = z.object({
  totalTweetsInDb: z.number().int(),
  totalTweetsFetched: z.number().int(),
  totalTweetsRequested: z.number().int(),
  lastFetchAt: z.date().nullable(),
});

export type AdminTwitterUsageSummary = z.infer<typeof AdminTwitterUsageSummarySchema>;

export const AdminTwitterUsagePointSchema = z.object({
  day: z.string(),
  count: z.number().int(),
});

export type AdminTwitterUsagePoint = z.infer<typeof AdminTwitterUsagePointSchema>;

export const AdminTwitterUsagePointHourlySchema = z.object({
  hour: z.string(),
  count: z.number().int(),
});

export type AdminTwitterUsagePointHourly = z.infer<typeof AdminTwitterUsagePointHourlySchema>;

export const AdminTwitterXUsageSchema = z
  .object({
    data: z
      .object({
        projectCap: z.string().optional(),
        projectUsage: z.string().optional(),
        capResetDay: z.number().optional(),
      })
      .optional(),
  })
  .loose();

export type AdminTwitterXUsage = z.infer<typeof AdminTwitterXUsageSchema>;

export const AdminTwitterUsageResponseSchema = z.object({
  success: z.literal(true),
  summary: AdminTwitterUsageSummarySchema,
  xUsage: AdminTwitterXUsageSchema.nullable(),
  series: z.object({
    tweetsPerDay: z.array(AdminTwitterUsagePointSchema),
    fetchedPerDay: z.array(AdminTwitterUsagePointSchema),
  }),
  seriesHourly: z.object({
    tweetsPerHour: z.array(AdminTwitterUsagePointHourlySchema),
    fetchedPerHour: z.array(AdminTwitterUsagePointHourlySchema),
  }),
});

export type AdminTwitterUsageResponse = z.infer<typeof AdminTwitterUsageResponseSchema>;

export const AdminTwitterOAuthTokensResponseSchema = z.object({
  bearerToken: z.string(),
  refreshToken: z.string().nullable(),
  expiresAt: z.date().nullable(),
  twitterUserId: z.string().nullable(),
});

export type AdminTwitterOAuthTokensResponse = z.infer<typeof AdminTwitterOAuthTokensResponseSchema>;

export const AdminTwitterGraphqlManifestSourceSchema = z.object({
  source: z.string(),
  version: z.string(),
  priority: z.string(),
});

export type AdminTwitterGraphqlManifestSource = z.infer<typeof AdminTwitterGraphqlManifestSourceSchema>;

export const AdminTwitterGraphqlNormalizationSchema = z.object({
  key: z.string(),
  context: z.record(z.string(), z.unknown()).optional(),
});

export const AdminTwitterRestComparatorSchema = z.enum(['homeTimeline', 'userTimeline', 'mentionsTimeline', 'search']);
export type AdminTwitterRestComparator = z.infer<typeof AdminTwitterRestComparatorSchema>;
export type AdminTwitterGraphqlRestComparator = AdminTwitterRestComparator;

export const AdminTwitterGraphqlOperationSummarySchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().nullable().optional(),
  method: z.enum(['GET', 'POST']).default('GET'),
  queryId: z.string(),
  version: z.string(),
  normalization: AdminTwitterGraphqlNormalizationSchema.optional(),
  restComparator: AdminTwitterRestComparatorSchema.optional(),
  defaultVariables: z.record(z.string(), z.unknown()),
  defaultFeatures: z.record(z.string(), z.boolean()),
});

export type AdminTwitterGraphqlOperationSummary = z.infer<typeof AdminTwitterGraphqlOperationSummarySchema>;

export const AdminTwitterGraphqlOperationsResponseSchema = z.object({
  operations: z.array(AdminTwitterGraphqlOperationSummarySchema),
  manifest: z.array(AdminTwitterGraphqlManifestSourceSchema),
});

export type AdminTwitterGraphqlOperationsResponse = z.infer<typeof AdminTwitterGraphqlOperationsResponseSchema>;

export const AdminTwitterGraphqlExecuteBodySchema = z.object({
  cookies: z.string().min(1),
  operationKey: z.string(),
  variables: z.record(z.string(), z.unknown()).optional().default({}),
  featureOverrides: z.record(z.string(), z.boolean()).optional().default({}),
  includeNormalized: z.boolean().optional().default(true),
  includeRestComparison: z.boolean().optional().default(false),
});

export type AdminTwitterGraphqlExecuteBody = z.infer<typeof AdminTwitterGraphqlExecuteBodySchema>;

export const AdminTwitterNormalizedTweetSchema = z
  .object({
    id: z.string(),
    createdAt: z.coerce.date(),
  })
  .passthrough();

export const AdminTwitterGraphqlRestSummarySchema = z
  .object({
    comparator: AdminTwitterRestComparatorSchema.optional(),
    payload: z.unknown().optional(),
    summary: z
      .object({
        normalizedCount: z.number().int(),
        restCount: z.number().int(),
        sharedCount: z.number().int(),
        normalizedOnlyIds: z.array(z.string()),
        restOnlyIds: z.array(z.string()),
      })
      .optional(),
    error: z.string().optional(),
  })
  .nullable();

export const AdminTwitterGraphqlExecuteResponseSchema = z.object({
  success: z.literal(true),
  timestamp: z.date(),
  durationMs: z.number().int(),
  operation: z.object({
    key: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    method: z.enum(['GET', 'POST']),
    queryId: z.string(),
    version: z.string(),
  }),
  request: z.object({
    variables: z.record(z.string(), z.unknown()),
    featureFlags: z.record(z.string(), z.boolean()),
  }),
  manifest: z.object({
    sources: z.array(AdminTwitterGraphqlManifestSourceSchema),
  }),
  raw: z.unknown(),
  normalized: z
    .object({
      count: z.number().int(),
      tweets: z.array(AdminTwitterNormalizedTweetSchema).optional(),
    })
    .nullable(),
  normalizationError: z.string().nullable(),
  rest: AdminTwitterGraphqlRestSummarySchema,
});

export type AdminTwitterGraphqlExecuteResponse = z.infer<typeof AdminTwitterGraphqlExecuteResponseSchema>;

export const AdminTwitterFetchTimelineSourceSchema = z.enum(['home_timeline', 'user_timeline', 'search']);

export const AdminTwitterFetchTimelineRequestSchema = z.object({
  source: AdminTwitterFetchTimelineSourceSchema,
  username: z.string().trim().min(1).optional(),
  count: z.coerce.number().int().min(1).max(3200).default(1000),
  searchQuery: z.string().trim().min(1).optional(),
});

export type AdminTwitterFetchTimelineRequest = z.infer<typeof AdminTwitterFetchTimelineRequestSchema>;

export const AdminTwitterFetchTimelineResultsSchema = z.object({
  newTweets: z.number().int(),
  updatedTweets: z.number().int(),
  newUsers: z.number().int(),
  updatedUsers: z.number().int(),
  alreadyHad: z.number().int(),
  errors: z.number().int(),
});
export type AdminTwitterFetchTimelineResults = z.infer<typeof AdminTwitterFetchTimelineResultsSchema>;

export const AdminTwitterFetchTimelineResponseSchema = z.object({
  id: z.string(),
  source: AdminTwitterFetchTimelineSourceSchema,
  startedAt: z.date(),
  completedAt: z.date(),
  requested: z.number().int(),
  username: z.string().nullable().optional(),
  searchQuery: z.string().nullable().optional(),
  results: AdminTwitterFetchTimelineResultsSchema,
  metadata: z.object({
    duration: z.number().int(),
    endpoint: z.string(),
  }),
});

export type AdminTwitterFetchTimelineResponse = z.infer<typeof AdminTwitterFetchTimelineResponseSchema>;

export const AdminTwitterPerformanceSampleSchema = z.object({
  ms: z.number(),
  status: z.number().int(),
  rateLimit: z
    .object({
      limit: z.number().nullable().optional(),
      remaining: z.number().nullable().optional(),
      reset: z.number().nullable().optional(),
    })
    .optional(),
});
export type AdminTwitterPerformanceSample = z.infer<typeof AdminTwitterPerformanceSampleSchema>;

export const AdminTwitterPerformanceEndpointSchema = z.object({
  target: z.enum(['tweetLookup', 'tweetById', 'userByUsername', 'usersByIds', 'recentSearch']),
  url: z.string(),
  n: z.number().int(),
  p50Ms: z.number().int(),
  p95Ms: z.number().int(),
  samples: z.array(AdminTwitterPerformanceSampleSchema),
});
export type AdminTwitterPerformanceEndpoint = z.infer<typeof AdminTwitterPerformanceEndpointSchema>;

export const AdminTwitterPerformanceResponseSchema = z.object({
  region: z.string().optional(),
  runtime: z.enum(['nodejs', 'edge']).optional(),
  results: z.array(AdminTwitterPerformanceEndpointSchema),
  note: z.string(),
});

export type AdminTwitterPerformanceResponse = z.infer<typeof AdminTwitterPerformanceResponseSchema>;

export const AdminTwitterLookupQuerySchema = z.object({
  input: z.string().trim().min(1),
});

export type AdminTwitterLookupQuery = z.infer<typeof AdminTwitterLookupQuerySchema>;

export const AdminTwitterLookupResponseSchema = z.object({
  input: z.object({
    original: z.string(),
    tweetId: z.string(),
  }),
  api: z.object({
    tweets: z.array(z.unknown()),
    includes: z.record(z.string(), z.unknown()).nullable().optional(),
  }),
  validated: z.object({
    tweet: z.unknown().nullable(),
    includes: z.unknown().nullable(),
  }),
  timestamp: z.date(),
});

export type AdminTwitterLookupResponse = z.infer<typeof AdminTwitterLookupResponseSchema>;

export const ADMIN_TWITTER_FAILURE_REASONS = [
  'forced-timeout',
  'rate-limit',
  'unauthorized',
  'invalid-response',
  'upstream-error',
  'client-error',
  'unknown-error',
] as const;

export const AdminTwitterFailureReasonSchema = z.enum(ADMIN_TWITTER_FAILURE_REASONS);
export type AdminTwitterFailureReason = z.infer<typeof AdminTwitterFailureReasonSchema>;

export const AdminTwitterApiCallSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  userEmail: z.string().nullable(),
  userTwitterUsername: z.string().nullable(),
  service: z.string().nullable(),
  operation: z.string().nullable(),
  endpoint: z.string(),
  method: z.string(),
  requestParams: z.record(z.string(), z.unknown()).nullable(),
  requestMetadata: z.record(z.string(), z.unknown()).nullable(),
  sourceComponent: z.string().nullable(),
  responseData: z.record(z.string(), z.unknown()).nullable(),
  responseHeaders: z.record(z.string(), z.unknown()).nullable(),
  responseMetadata: z.record(z.string(), z.unknown()).nullable(),
  statusCode: z.number().nullable(),
  tweetsRequested: z.number().nullable(),
  tweetsReceived: z.number().nullable(),
  responseTimeMs: z.number().nullable(),
  dataEfficiency: z.number().nullable(),
  excludeReplies: z.boolean(),
  excludeRetweets: z.boolean(),
  success: z.boolean(),
  errorMessage: z.string().nullable(),
  errorCode: z.string().nullable(),
  failureReason: AdminTwitterFailureReasonSchema.nullable(),
  rateLimitRemaining: z.number().nullable(),
  rateLimitReset: z.date().nullable(),
  createdAt: z.date(),
});

export type AdminTwitterApiCall = z.infer<typeof AdminTwitterApiCallSchema>;

export const AdminTwitterPaginationSchema = z.object({
  page: z.number().int(),
  limit: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
});

export const AdminTwitterApiCallsResponseSchema = z.object({
  calls: z.array(AdminTwitterApiCallSchema),
  pagination: AdminTwitterPaginationSchema,
});

export type AdminTwitterApiCallsResponse = z.infer<typeof AdminTwitterApiCallsResponseSchema>;

const ADMIN_TWITTER_SORT_FIELDS = [
  'userTwitterUsername',
  'success',
  'endpoint',
  'service',
  'responseTimeMs',
  'payloadEfficiency',
  'dataEfficiency',
  'createdAt',
] as const;

const AdminTwitterBaseListQuerySchema = createListQuerySchema(ADMIN_TWITTER_SORT_FIELDS, {
  defaultSortBy: 'createdAt',
});

export const AdminTwitterApiCallsQuerySchema = AdminTwitterBaseListQuerySchema.extend({
  endpoint: z.string().trim().optional(),
  userId: z.string().trim().optional(),
  success: z.enum(['all', 'true', 'false']).default('all'),
  service: z.string().trim().default('all'),
  timeRange: z.enum(['5m', '15m', '60m', '3h', '6h', '12h', '24h', '3d', '7d', 'all']).default('all'),
  failureReason: AdminTwitterFailureReasonSchema.optional(),
  statusCode: z.coerce.number().int().min(100).max(599).optional(),
});

export type AdminTwitterApiCallsQuery = z.infer<typeof AdminTwitterApiCallsQuerySchema>;

export const AdminTwitterApiCallsDeleteQuerySchema = z.object({
  id: z.string().trim().optional(),
});

export type AdminTwitterApiCallsDeleteQuery = z.infer<typeof AdminTwitterApiCallsDeleteQuerySchema>;

export const AdminTwitterApiCallsDeleteResponseSchema = z.object({
  success: z.literal(true),
  message: z.string().optional(),
  deletedCount: z.number().int().optional(),
});

export type AdminTwitterApiCallsDeleteResponse = z.infer<typeof AdminTwitterApiCallsDeleteResponseSchema>;

export const AdminTwitterApiCallStatsResponseSchema = z.object({
  overallStats: z
    .object({
      totalCalls: z.number().int(),
      successfulCalls: z.number().int(),
      failedCalls: z.number().int(),
      avgResponseTime: z.number().nullable(),
      maxResponseTime: z.number().nullable(),
      minResponseTime: z.number().nullable(),
      avgEfficiency: z.number().nullable(),
      forcedTimeouts: z.number().int(),
    })
    .nullable(),
  endpointStats: z.array(
    z.object({
      endpoint: z.string(),
      calls: z.number().int(),
      avgResponseTime: z.number().nullable(),
      avgEfficiency: z.number().nullable(),
      failures: z.number().int(),
      forcedTimeouts: z.number().int(),
    })
  ),
  hourlyTrend: z.array(
    z.object({
      hour: z.string(),
      calls: z.number().int(),
      avgResponseTime: z.number().nullable(),
    })
  ),
  recentErrors: z.array(
    z.object({
      endpoint: z.string(),
      service: z.string().nullable(),
      errorMessage: z.string().nullable(),
      errorCode: z.string().nullable(),
      failureReason: AdminTwitterFailureReasonSchema.nullable(),
      createdAt: z.date(),
    })
  ),
  slowestEndpoints: z.array(
    z.object({
      endpoint: z.string(),
      avgResponseTime: z.number().nullable(),
      calls: z.number().int(),
    })
  ),
});

export type AdminTwitterApiCallStatsResponse = z.infer<typeof AdminTwitterApiCallStatsResponseSchema>;

export const AdminTwitterApiCallStatsRequestSchema = z.object({
  service: z.string().optional(),
});
export type AdminTwitterApiCallStatsRequest = z.infer<typeof AdminTwitterApiCallStatsRequestSchema>;
